import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { completeReport } from "@/lib/offlineApi";
import { getCachedSchedule } from "@/lib/offlineStore";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, Eraser, PenSquare, Clock, Users, Sparkles, Loader2, Lock, RotateCcw, Camera, Mic, MicOff } from "lucide-react";
import { computeServiceTimes, formatTime } from "@/lib/serviceTime";
import SignaturePad from "@/components/ResponsiveSignaturePad";
import { VoiceTextarea, VoiceInput, VoiceButton } from "@/components/VoiceInput";
import { PhotoCapture } from "@/components/PhotoCapture";
import { PhotoReportMIP } from "@/components/PhotoReportMIP";
import { PhotoAnnotator } from "@/components/PhotoAnnotator";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const EXTERIOR = [
    { k: "grass_cut",               label: "¿El césped está cortado y en orden?",                          finding: "Césped sin cortar o en desorden" },
    { k: "drains_good",             label: "¿Los drenajes y sifones están en buen estado?",                finding: "Drenajes o sifones en mal estado" },
    { k: "machinery_organized",     label: "¿La maquinaria en desuso está ordenada o guardada?",           finding: "Maquinaria en desuso sin ordenar o guardar" },
    { k: "debris_organized",        label: "¿Los escombros y materiales están ordenados?",                 finding: "Escombros o materiales sin ordenar" },
    { k: "grate_screens",           label: "¿Los tragantes tienen zaranda instalada?",                     finding: "Tragantes sin zaranda instalada" },
    { k: "trash_containers_closed", label: "¿Los contenedores de basura están cerrados?",                  finding: "Contenedores de basura abiertos" },
    { k: "waste_management",        label: "¿Los desperdicios se manejan de forma adecuada?",              finding: "Manejo inadecuado de desperdicios" },
    { k: "nesting_sites_found",     label: "¿Hay ausencia de lugares de anidación de plagas?",             finding: "Posibles lugares de anidación de plagas detectados" },
];
const INTERIOR = [
    { k: "walls_clean",             label: "¿Las paredes están limpias y sin grietas?",                    finding: "Paredes sucias o con grietas" },
    { k: "floors_clean",            label: "¿Los pisos están limpios?",                                    finding: "Pisos con suciedad o residuos" },
    { k: "ceilings_no_grease",      label: "¿Los techos están libres de acumulación de grasas?",           finding: "Techos con acumulación de grasas" },
    { k: "machinery_no_residue",    label: "¿La maquinaria está libre de residuos orgánicos?",             finding: "Maquinaria con residuos orgánicos" },
    { k: "window_screens_good",     label: "¿Las mallas de las ventanas están en buenas condiciones?",     finding: "Mallas de ventanas en mal estado o dañadas" },
    { k: "doors_sealed",            label: "¿Las puertas permanecen cerradas y selladas?",                 finding: "Puertas sin cerrar o mal selladas" },
    { k: "general_cleaning_ok",     label: "¿La limpieza general es satisfactoria?",                       finding: "Limpieza general insatisfactoria" },
    { k: "nesting_sites_found",     label: "¿Hay ausencia de lugares de anidación de plagas?",             finding: "Posibles lugares de anidación de plagas detectados" },
];
const FOOD = [
    { k: "storage_adequate",        label: "¿Los productos están almacenados adecuadamente?",              finding: "Productos con almacenamiento inadecuado" },
    { k: "surfaces_clean",          label: "¿Las superficies y anexos están limpios?",                     finding: "Superficies o anexos con suciedad" },
    { k: "spaces_behind_clean",     label: "¿Los espacios debajo y detrás de los muebles están limpios?",  finding: "Espacios debajo y detrás de muebles con suciedad" },
    { k: "all_areas_accessible",    label: "¿Todas las áreas están accesibles para inspección?",           finding: "Áreas con acceso restringido para inspección" },
    { k: "structures_good",         label: "¿Las estructuras están en buen estado?",                       finding: "Estructuras en mal estado o deterioradas" },
    { k: "regular_cleaning",        label: "¿Hay evidencia de limpieza regular?",                          finding: "Sin evidencia de limpieza regular" },
    { k: "nesting_sites_found",     label: "¿Hay ausencia de lugares de anidación de plagas?",             finding: "Posibles lugares de anidación de plagas detectados" },
];
const METHODS = ["ASP", "NEB", "GRA", "POL", "GEL", "GAS", "MIC", "CEB", "TRA", "PEG", "LAM", "KALL"];
const SURVEY_QUESTIONS = [
    { k: "punctuality_rating", label: "Puntualidad" },
    { k: "friendliness_rating", label: "Amabilidad" },
    { k: "efficacy_rating", label: "Eficacia" },
];
const EMOJI = { 1: "🙁", 2: "😐", 3: "🙂" };
const EMOJI_LABEL = { 1: "Malo", 2: "Regular", 3: "Bueno" };

function ToggleSiNo({ value, onChange, testIdPrefix }) {
    // ON = Sí (true), OFF = No (false). Default is OFF.
    const isOn = value === true;
    return (
        <div className="flex items-center justify-between mt-2 px-1">
            <span className={`text-xs uppercase tracking-wider font-semibold ${isOn ? "text-muted-foreground" : "text-black"}`}>NO</span>
            <Switch
                checked={isOn}
                onCheckedChange={(v) => onChange(v === true)}
                className="data-[state=checked]:bg-[#FFD62B] data-[state=unchecked]:bg-zinc-300 mx-3"
                data-testid={`${testIdPrefix}-switch`}
            />
            <span className={`text-xs uppercase tracking-wider font-semibold ${isOn ? "text-black" : "text-muted-foreground"}`}>SÍ</span>
        </div>
    );
}

export default function TechReportFlow() {
    const { id } = useParams();
    const nav = useNavigate();
    const [step, setStep] = useState(-1); // -1 = Paso 0 (Photo Report), 0..6 = pasos 1-7
    const [service, setService] = useState(null);
    const [client, setClient] = useState(null);
    // Initialize all inspection items to false (OFF) by default
    const initFalse = (arr) => Object.fromEntries(arr.map((x) => [x.k, false]));
    const [exterior, setExterior] = useState(() => initFalse(EXTERIOR));
    const [interior, setInterior] = useState(() => initFalse(INTERIOR));
    const [food, setFood] = useState(() => initFalse(FOOD));
    const [reincidentChecks, setReincidentChecks] = useState(new Set());
    const [notes, setNotes] = useState({ areas_treated: "", recommendations: "", problem_areas: "", corrective_actions: "", follow_ups: "" });
    const [products, setProducts] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [survey, setSurvey] = useState({});
    const [acceptedBy, setAcceptedBy] = useState("");
    const [paymentType, setPaymentType] = useState("");
    const [followUp, setFollowUp] = useState(false);
    const [followUpPeriod, setFollowUpPeriod] = useState("");
    const [followUpOther, setFollowUpOther] = useState("");
    const [followUpDate, setFollowUpDate] = useState(""); // YYYY-MM-DD

    const calcFollowUpDate = (period) => {
        if (!period || period === "otro") return "";
        const days = period === "8_dias" ? 8 : 15;
        const d = new Date();
        d.setDate(d.getDate() + days);
        return d.toISOString().split("T")[0];
    };

    const formatDateDMY = (iso) => {
        if (!iso) return "";
        const [y, m, day] = iso.split("-");
        return `${day}/${m}/${y}`;
    };
    const [mispriced, setMispriced] = useState(false);
    const sigRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const [confirmNoProducts, setConfirmNoProducts] = useState(false);
    const [improvingField, setImprovingField] = useState(null); // key of field being improved

    const AI_FIELDS = { recommendations: "recommendations", corrective_actions: "corrective_actions" };

    const improveWithAI = async (fieldKey) => {
        const text = notes[fieldKey];
        if (!text?.trim()) return toast.error("Escribe algo primero para poder mejorarlo");
        setImprovingField(fieldKey);
        const token = localStorage.getItem("trulyn_token");
        try {
            const resp = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/ai/improve-text`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ field_type: AI_FIELDS[fieldKey], text, service_type: service?.service_type_flag || "" }),
                }
            );
            if (!resp.ok) throw new Error("Error del servidor");
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let accumulated = "";
            setNotes((n) => ({ ...n, [fieldKey]: "" }));
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                for (const line of chunk.split("\n")) {
                    if (!line.startsWith("data: ")) continue;
                    const raw = line.slice(6).trim();
                    if (raw === "[DONE]") break;
                    try {
                        const parsed = JSON.parse(raw);
                        if (parsed.error) throw new Error(parsed.error);
                        if (parsed.content) {
                            accumulated = accumulated + parsed.content;
                            const snap = accumulated;
                            setNotes((n) => ({ ...n, [fieldKey]: snap }));
                        }
                    } catch (_e) { /* non-JSON SSE line — skip */ }
                }
            }
            toast.success("Texto mejorado con IA");
        } catch (e) {
            toast.error("No se pudo mejorar el texto");
        } finally {
            setImprovingField(null);
        }
    };

    useEffect(() => {
        api.get(`/schedules/${id}`)
            .then((r) => {
                setService(r.data);
                // Pre-load assigned supplies into Step 5 with quantity 0
                if (r.data?.supplies_assigned?.length > 0) {
                    setProducts(
                        r.data.supplies_assigned.map((s) => ({
                            product_name: s.product || "",
                            quantity: 0,
                            unit: s.unit || "",
                            dose: s.dose || "",
                            method: s.method || "ASP",
                            pest_target: s.pest_target || "",
                            is_preset: true,
                        }))
                    );
                }
                if (r.data?.client_id) {
                    api.get(`/clients`)
                        .then((cr) => setClient((cr.data || []).find((x) => x.id === r.data.client_id) || null))
                        .catch(() => {});
                }
            })
            .catch(async () => {
                const cached = await getCachedSchedule(id);
                if (cached) {
                    setService(cached);
                    if (cached?.supplies_assigned?.length > 0) {
                        setProducts(
                            cached.supplies_assigned.map((s) => ({
                                product_name: s.product || "",
                                quantity: 0,
                                unit: s.unit || "",
                                dose: s.dose || "",
                                method: s.method || "ASP",
                                pest_target: s.pest_target || "",
                                is_preset: true,
                            }))
                        );
                    }
                } else {
                    toast.error("Error");
                }
            });
    }, [id]);

    const STEPS = ["Exterior", "Interior", "Bodega", "Notas", "Productos", "Pago", "Resumen y firma"];
    const progress = step === -1 ? 0 : ((step + 1) / STEPS.length) * 100;
    const stepLabel = step === -1 ? "Reporte fotográfico (Opcional)" : STEPS[step];
    const stepCounter = step === -1 ? `0 de ${STEPS.length}` : `${step + 1} de ${STEPS.length}`;

    const completedCount = (items, data) => items.filter((i) => data[i.k] === true || data[i.k] === false).length;

    const next = () => {
        // Step 4 (index 3): Notes — all fields required
        if (step === 3) {
            const missing = [];
            if (!notes.areas_treated?.trim()) missing.push("Áreas tratadas");
            if (!notes.recommendations?.trim()) missing.push("Recomendaciones");
            if (!notes.problem_areas?.trim()) missing.push("Áreas con problemas");
            if (!notes.corrective_actions?.trim()) missing.push("Acciones correctivas");
            if (missing.length) return toast.error(`Complete: ${missing.join(", ")}`);
        }
        // Step 5 (index 4): Products — quantity required; pest_target required only if quantity > 0
        if (step === 4 && products.length > 0) {
            for (let i = 0; i < products.length; i++) {
                const p = products[i];
                if (p.quantity === "" || p.quantity === undefined || p.quantity === null) {
                    return toast.error(`Producto #${i + 1}: ingrese la cantidad (puede ser 0)`);
                }
                if (Number(p.quantity) > 0 && !p.pest_target?.trim()) {
                    return toast.error(`Producto #${i + 1} (${p.product_name || "sin nombre"}): ingrese la plaga objetivo`);
                }
            }
        }
        // Step 6 (index 5): Payment type required
        if (step === 5) {
            if (!paymentType) return toast.error("Seleccione el tipo de pago");
        }
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
    };
    const prev = () => setStep((s) => Math.max(-1, s - 1));

    const addProduct = () => setProducts((p) => [...p, { product_name: "", quantity: 1, unit: "", dose: "", method: "ASP", pest_target: "", is_preset: false }]);
    const rmProduct = (i) => setProducts((p) => p.filter((_, idx) => idx !== i));
    const updProduct = (i, k, v) => setProducts((p) => p.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

    const submit = async () => {
        if (!acceptedBy.trim()) return toast.error("Indique quién recibe el servicio");
        if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Falta la firma del cliente");
        await doSubmit();
    };

    const toggleReincident = (fullKey, checked) => {
        setReincidentChecks((prev) => {
            const next = new Set(prev);
            if (checked) next.add(fullKey); else next.delete(fullKey);
            return next;
        });
    };

    const doSubmit = async () => {
        const pad = sigRef.current;
        if (!pad) return toast.error("No se pudo capturar la firma");
        // Double-check the canvas was actually drawn on (not just whitespace)
        if (pad.isEmpty()) return toast.error("Falta la firma del cliente");
        const sig = pad.toDataURL("image/png");
        if (!sig || sig.length < 200) {
            return toast.error("No se pudo capturar la firma. Vuelva a firmar.");
        }
        setSubmitting(true);
        try {
            const payload = {
                scheduled_service_id: id,
                exterior, interior, food_storage: food,
                ...notes,
                products_used: products.map((p) => ({ ...p, quantity: Number(p.quantity) || 0 })),
                survey,
                client_signature_base64: sig,
                accepted_by_name: acceptedBy.trim(),
                photos: photos.map((p) => ({ base64: p.base64, caption: p.caption || null })),
                payment_type: paymentType,
                follow_up_scheduled: followUp,
                follow_up_period: followUp ? followUpPeriod : null,
                follow_up_date: followUp && followUpDate ? formatDateDMY(followUpDate) : null,
                follow_up_other: followUp && followUpPeriod ? followUpOther.trim() : null,
                service_mispriced: mispriced,
                reincident_checks: [...reincidentChecks],
                _service_date: service?.scheduled_date,
            };
            const res = await completeReport(id, payload);
            if (res.online) {
                toast.success("Servicio completado y reporte sincronizado");
                // Trigger background photo report processing (fire and forget)
                try {
                    await api.post(`/photo-reports/${id}/process`);
                } catch (_) { /* silent — optional feature */ }
            } else {
                toast.warning("Reporte guardado localmente. Se sincronizará y generará el recibo automáticamente al recuperar conexión.");
            }
            nav(`/tech/service/${id}/receipt/${res.report.id}`, { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally {
            setSubmitting(false);
        }
    };

    if (!service) return <div className="p-6">Cargando...</div>;

    return (
        <div className="px-4 pt-3 pb-32" data-testid="tech-report-flow">
            <Link to={`/tech/service/${id}`} className="inline-flex items-center text-sm text-muted-foreground mb-2 hover:text-foreground">
                <ArrowLeft className="w-4 h-4 mr-1" /> Detalle
            </Link>

            {/* Sticky progress */}
            <div className="sticky top-[56px] z-10 bg-background pb-2">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{stepLabel}</span>
                    <span className="font-mono">{stepCounter}</span>
                </div>
                <Progress value={progress} className="h-2" />
            </div>

            {/* Step content */}
            <div className="mt-4">
                {step === -1 && (
                    <PhotoReportMIP serviceId={id} />
                )}
                {step === 0 && (
                    <SectionToggleList
                        title="Condiciones Exteriores"
                        items={EXTERIOR}
                        data={exterior}
                        onChange={(k, v) => setExterior((d) => ({ ...d, [k]: v }))}
                        completedLabel={`${completedCount(EXTERIOR, exterior)}/${EXTERIOR.length} contestadas`}
                        prefix="exterior"
                        reincidentChecks={reincidentChecks}
                        onReincidentToggle={toggleReincident}
                    />
                )}
                {step === 1 && (
                    <SectionToggleList
                        title="Condiciones Interiores – General"
                        items={INTERIOR}
                        data={interior}
                        onChange={(k, v) => setInterior((d) => ({ ...d, [k]: v }))}
                        completedLabel={`${completedCount(INTERIOR, interior)}/${INTERIOR.length} contestadas`}
                        prefix="interior"
                        reincidentChecks={reincidentChecks}
                        onReincidentToggle={toggleReincident}
                    />
                )}
                {step === 2 && (
                    <SectionToggleList
                        title="Bodega de Alimentos"
                        items={FOOD}
                        data={food}
                        onChange={(k, v) => setFood((d) => ({ ...d, [k]: v }))}
                        completedLabel={`${completedCount(FOOD, food)}/${FOOD.length} contestadas`}
                        prefix="food"
                        reincidentChecks={reincidentChecks}
                        onReincidentToggle={toggleReincident}
                    />
                )}
                {step === 3 && (
                    <Card className="p-4 space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-semibold text-lg">Notas del servicio</h2>
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent))]" />
                                Dictado por voz disponible
                            </span>
                        </div>
                        {[
                            { k: "areas_treated", label: "Áreas tratadas" },
                            { k: "recommendations", label: "Recomendaciones" },
                            { k: "problem_areas", label: "Áreas con problemas" },
                            { k: "corrective_actions", label: "Acciones correctivas" },
                        ].map((f) => (
                            <div key={f.k}>
                                <div className="flex items-center justify-between mb-1.5">
                                    <Label htmlFor={f.k}>{f.label}</Label>
                                    {AI_FIELDS[f.k] && (
                                        <button
                                            type="button"
                                            onClick={() => improveWithAI(f.k)}
                                            disabled={!!improvingField}
                                            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[#FFD62B] bg-[#FFD62B]/10 text-black hover:bg-[#FFD62B]/30 disabled:opacity-50 transition-colors"
                                            data-testid={`ai-improve-${f.k}`}
                                            title="Mejorar redacción con IA"
                                        >
                                            {improvingField === f.k
                                                ? <><Loader2 className="w-3 h-3 animate-spin" /> Mejorando...</>
                                                : <><Sparkles className="w-3 h-3" /> Mejorar con IA</>
                                            }
                                        </button>
                                    )}
                                </div>
                                <VoiceTextarea
                                    id={f.k}
                                    value={notes[f.k]}
                                    onChange={(v) => setNotes((n) => ({ ...n, [f.k]: v }))}
                                    className="min-h-20"
                                    rows={3}
                                    placeholder="Describa o toque el micrófono para dictar..."
                                    testId={`notes-${f.k}`}
                                />
                            </div>
                        ))}
                    </Card>
                )}
                {step === 4 && (
                    <Card className="p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="font-semibold text-lg">Productos utilizados</h2>
                            <Button
                                size="sm"
                                onClick={addProduct}
                                disabled={!service?.allow_add_products}
                                className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="products-add-button"
                                title={!service?.allow_add_products ? "El administrador debe autorizar agregar productos para este servicio" : ""}
                            >
                                {service?.allow_add_products
                                    ? <><Plus className="w-4 h-4 mr-1" /> Agregar</>
                                    : <><Lock className="w-3.5 h-3.5 mr-1" /> Agregar</>
                                }
                            </Button>
                        </div>
                        {!service?.allow_add_products && (
                            <div className="flex items-start gap-2 rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2.5 mb-3 text-xs text-zinc-500">
                                <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-zinc-400" />
                                <span>Solo puedes editar las cantidades de los insumos asignados. Para agregar productos adicionales, solicita autorización al administrador.</span>
                            </div>
                        )}
                        {products.length === 0 && (
                            <p className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-lg">No hay productos agregados</p>
                        )}
                        <div className="space-y-3">
                            {products.map((p, i) => (
                                p.is_preset ? (
                                    /* ── Producto pre-asignado: solo cantidad editable ── */
                                    <div key={i} className="border rounded-lg p-3 bg-zinc-50/60" data-testid={`product-row-${i}`}>
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <div>
                                                <p className="font-semibold text-sm leading-tight">{p.product_name}</p>
                                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                    {p.dose && (
                                                        <span className="inline-flex items-center gap-1 text-[11px] bg-white border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600">
                                                            <span className="font-medium text-zinc-400">Dosis</span> {p.dose}
                                                        </span>
                                                    )}
                                                    {p.method && (
                                                        <span className="inline-flex items-center gap-1 text-[11px] bg-white border border-zinc-200 rounded px-1.5 py-0.5 text-zinc-600">
                                                            <span className="font-medium text-zinc-400">Método</span> {p.method}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <Badge variant="outline" className="font-mono shrink-0">#{i + 1}</Badge>
                                        </div>
                                        <div className="flex items-end gap-2">
                                            <div className="flex-1">
                                                <Label className="text-xs font-semibold">Cantidad utilizada <span className="text-red-500">*</span></Label>
                                                <Input
                                                    type="number"
                                                    step="0.1"
                                                    min="0"
                                                    value={p.quantity}
                                                    onChange={(e) => updProduct(i, "quantity", e.target.value)}
                                                    className="h-11 mt-1 text-base font-semibold"
                                                    data-testid={`product-${i}-qty`}
                                                />
                                            </div>
                                            {p.unit && (
                                                <div className="h-11 flex items-center px-3 rounded-md border bg-white text-sm font-semibold text-zinc-700 shrink-0">
                                                    {p.unit}
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-2">
                                            <Label className="text-xs font-semibold">Plaga objetivo</Label>
                                            <VoiceTextarea
                                                value={p.pest_target || ""}
                                                onChange={(v) => updProduct(i, "pest_target", v)}
                                                placeholder="Ej: Cucarachas, roedores, hormigas..."
                                                rows={2}
                                                className="mt-1 text-sm resize-none"
                                                testId={`product-${i}-pest`}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    /* ── Producto agregado manualmente: todos los campos editables ── */
                                    <div key={i} className="border rounded-lg p-3 space-y-2 bg-background border-[#FFD62B]/40" data-testid={`product-row-${i}`}>
                                        <div className="flex items-center justify-between">
                                            <Badge className="bg-[#FFD62B]/20 text-black border border-[#FFD62B]/50 font-mono text-xs">#{i + 1} Agregado</Badge>
                                            <button onClick={() => rmProduct(i)} className="text-red-500 p-1" data-testid={`product-remove-${i}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Producto</Label>
                                            <VoiceInput value={p.product_name} onChange={(v) => updProduct(i, "product_name", v)} placeholder="Nombre del producto" testId={`product-${i}-name`} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <Label className="text-xs">Cantidad</Label>
                                                <Input type="number" step="0.1" min="0" value={p.quantity} onChange={(e) => updProduct(i, "quantity", e.target.value)} data-testid={`product-${i}-qty`} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Unidad</Label>
                                                <Input value={p.unit} onChange={(e) => updProduct(i, "unit", e.target.value)} placeholder="litros, gr..." data-testid={`product-${i}-unit`} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <Label className="text-xs">Dosis</Label>
                                                <VoiceInput value={p.dose} onChange={(v) => updProduct(i, "dose", v)} placeholder="30 ml/L" testId={`product-${i}-dose`} />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Método</Label>
                                                <Select value={p.method} onValueChange={(v) => updProduct(i, "method", v)}>
                                                    <SelectTrigger data-testid={`product-${i}-method`}><SelectValue /></SelectTrigger>
                                                    <SelectContent>
                                                        {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div>
                                            <Label className="text-xs">Plaga objetivo</Label>
                                            <VoiceTextarea value={p.pest_target || ""} onChange={(v) => updProduct(i, "pest_target", v)} placeholder="Ej: Cucarachas, roedores, hormigas..." rows={2} className="text-sm resize-none mt-1" testId={`product-${i}-pest`} />
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    </Card>
                )}
                {step === 5 && (
                    <div className="space-y-4">
                        <Card className="p-4" data-testid="payment-followup-card">
                            <h2 className="font-semibold text-lg mb-3">Pago y seguimiento</h2>
                            <div className="mb-4">
                                <Label htmlFor="payment_type">Tipo de pago <span className="text-red-600">*</span></Label>
                                <Select value={paymentType} onValueChange={setPaymentType}>
                                    <SelectTrigger id="payment_type" className="h-11 mt-1.5" data-testid="payment-type-select">
                                        <SelectValue placeholder="Seleccione el método de pago" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="efectivo">Efectivo</SelectItem>
                                        <SelectItem value="cheque">Cheque</SelectItem>
                                        <SelectItem value="pos">POS</SelectItem>
                                        <SelectItem value="enlace">Enlace</SelectItem>
                                        <SelectItem value="credito">Crédito</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="border-t pt-4">
                                <div className="flex items-center justify-between mb-1">
                                    <Label className="mb-0">¿Programar refuerzo?</Label>
                                    <Switch
                                        checked={followUp}
                                        onCheckedChange={(v) => { setFollowUp(v); if (!v) { setFollowUpPeriod(""); setFollowUpOther(""); } }}
                                        className="data-[state=checked]:bg-[#FFD62B]"
                                        data-testid="follow-up-switch"
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground">{followUp ? "Visita bajo garantía" : "No es necesario"}</p>

                                {followUp && (
                                    <div className="mt-3 space-y-3">
                                        <div>
                                            <Label htmlFor="follow_up_date">Fecha del refuerzo <span className="text-red-600">*</span></Label>
                                            <input
                                                id="follow_up_date"
                                                type="date"
                                                value={followUpDate}
                                                min={new Date().toISOString().split("T")[0]}
                                                onChange={(e) => { setFollowUpDate(e.target.value); if (!followUpPeriod) setFollowUpPeriod("calendario"); }}
                                                className="w-full mt-1.5 h-11 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                                data-testid="follow-up-date-input"
                                            />
                                            {followUpDate && (
                                                <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#FFD62B]/15 border border-[#FFD62B]/40 px-3 py-2">
                                                    <span className="text-sm font-semibold text-black">
                                                        Visita bajo garantía: {formatDateDMY(followUpDate)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <Label htmlFor="follow_up_other">Razón del refuerzo <span className="text-red-600">*</span></Label>
                                            <VoiceTextarea
                                                id="follow_up_other"
                                                value={followUpOther}
                                                onChange={setFollowUpOther}
                                                placeholder="Describa el motivo del refuerzo... (toque el mic para dictar)"
                                                rows={3}
                                                className="mt-1.5"
                                                testId="follow-up-other-input"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Alerta de precio: switch ROJO cuando está encendido */}
                            <div className="border-t pt-4 mt-4">
                                <div className="flex items-center justify-between mb-1">
                                    <Label className="mb-0 leading-snug pr-3">¿El servicio está mal cobrado?</Label>
                                    <Switch
                                        checked={mispriced}
                                        onCheckedChange={setMispriced}
                                        className="data-[state=checked]:bg-[#FF0000] data-[state=unchecked]:bg-zinc-300"
                                        data-testid="mispriced-switch"
                                    />
                                </div>
                                {mispriced ? (
                                    <div className="mt-2 rounded-md border border-[#FF0000]/30 bg-[#FF0000]/5 px-3 py-2">
                                        <p className="text-xs font-semibold text-[#FF0000] uppercase tracking-wider">Alerta enviada</p>
                                        <p className="text-xs text-foreground/80 mt-0.5">Has indicado que el precio podría estar mal (bajo o no acorde al servicio). Administración revisará.</p>
                                    </div>
                                ) : (
                                    <p className="text-xs text-muted-foreground">Apagado: el precio está bien. Enciende solo si crees que el servicio está barato o mal cobrado.</p>
                                )}
                            </div>
                        </Card>
                    </div>
                )}
                {step === 6 && (
                    <div className="space-y-4">
                        <ExecutiveSummary
                            service={service}
                            client={client}
                            exterior={exterior}
                            interior={interior}
                            food={food}
                            notes={notes}
                            products={products}
                            photos={photos}
                            paymentType={paymentType}
                            followUp={followUp}
                            followUpPeriod={followUpPeriod}
                            followUpDate={followUpDate}
                            followUpOther={followUpOther}
                            reincidentChecks={reincidentChecks}
                        />

                        <Card className="p-4">
                            <h2 className="font-semibold text-lg mb-3">Encuesta de satisfacción</h2>
                            <p className="text-xs text-muted-foreground mb-3">Solicite al cliente que califique el servicio</p>
                            <div className="space-y-3">
                                {SURVEY_QUESTIONS.map((q) => (
                                    <div key={q.k} className="border rounded-lg p-3" data-testid={`survey-${q.k}`}>
                                        <div className="text-sm font-medium mb-2">{q.label}</div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[1, 2, 3].map((v) => (
                                                <button
                                                    key={v}
                                                    type="button"
                                                    onClick={() => setSurvey((s) => ({ ...s, [q.k]: v }))}
                                                    className={`rounded-lg border-2 py-2 transition-colors ${
                                                        survey[q.k] === v
                                                            ? "bg-[hsl(var(--accent))] border-[hsl(var(--accent))]"
                                                            : "border-border bg-white hover:border-[hsl(var(--accent))]"
                                                    }`}
                                                    data-testid={`survey-${q.k}-${EMOJI_LABEL[v].toLowerCase()}`}
                                                >
                                                    <div className="text-2xl">{EMOJI[v]}</div>
                                                    <div className="text-xs">{EMOJI_LABEL[v]}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card className="p-4">
                            <h2 className="font-semibold text-lg mb-3 flex items-center gap-2"><PenSquare className="w-5 h-5" /> Firma del cliente</h2>
                            <div className="mb-2 rounded-lg overflow-hidden border-2 border-dashed border-border bg-white">
                                <SignaturePad
                                    ref={sigRef}
                                    height={176}
                                    backgroundColor="#ffffff"
                                    penColor="#000000"
                                    canvasProps={{ className: "sig-canvas", "data-testid": "signature-canvas" }}
                                />
                            </div>
                            <div className="flex justify-between gap-2 mb-3">
                                <Button variant="outline" size="sm" onClick={() => sigRef.current?.clear()} data-testid="signature-clear-button">
                                    <Eraser className="w-4 h-4 mr-1" /> Borrar
                                </Button>
                                <span className="text-xs text-muted-foreground self-center">Firme con el dedo o lápiz</span>
                            </div>
                            <Label htmlFor="acceptedBy">Recibido por</Label>
                            <VoiceInput id="acceptedBy" value={acceptedBy} onChange={setAcceptedBy} placeholder="Nombre completo" className="mt-1" testId="accepted-by-input" />
                        </Card>
                    </div>
                )}
            </div>

            {/* Sticky bottom nav */}
            <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t z-20">
                <div className="max-w-2xl mx-auto px-4 py-3 grid grid-cols-2 gap-3">
                    <Button variant="outline" className="h-12" onClick={prev} disabled={step === -1 || submitting} data-testid="report-prev-button">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
                    </Button>
                    {step < STEPS.length - 1 ? (
                        <Button className="h-12 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold" onClick={next} data-testid="report-next-button">
                            Siguiente <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    ) : (
                        <Button className="h-12 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold" onClick={submit} disabled={submitting} data-testid="report-submit-button">
                            <Check className="w-4 h-4 mr-1" /> {submitting ? "Guardando..." : "Finalizar"}
                        </Button>
                    )}
                </div>
            </div>

            <AlertDialog open={confirmNoProducts} onOpenChange={setConfirmNoProducts}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Sin productos registrados</AlertDialogTitle>
                        <AlertDialogDescription>
                            No has agregado ningún producto utilizado. ¿Deseas continuar y finalizar el servicio sin registrar productos?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="no-products-cancel">Volver y agregar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => { setConfirmNoProducts(false); doSubmit(); }}
                            data-testid="no-products-continue"
                        >
                            Continuar sin productos
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}




function SectionToggleList({ title, items, data, onChange, completedLabel, prefix, reincidentChecks, onReincidentToggle }) {
    return (
        <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-lg">{title}</h2>
                <Badge variant="outline" className="font-mono">{completedLabel}</Badge>
            </div>
            <div className="space-y-1">
                {items.map((it) => {
                    const fullKey = `${prefix}.${it.k}`;
                    const isReincident = reincidentChecks?.has(fullKey);
                    return (
                        <div key={it.k} className={`border-b last:border-b-0 py-3 last:pb-0 rounded-lg px-2 transition-colors ${isReincident ? "bg-red-50" : ""}`}>
                            <div className="flex items-start justify-between gap-2">
                                <span className="text-sm font-medium leading-snug flex-1">{it.label}</span>
                                {isReincident && (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-100 rounded-full px-2 py-0.5 shrink-0">
                                        <RotateCcw className="w-2.5 h-2.5" /> REINCIDENTE
                                    </span>
                                )}
                            </div>
                            <ToggleSiNo
                                value={data[it.k]}
                                onChange={(v) => onChange(it.k, v)}
                                testIdPrefix={`${prefix}-${it.k}`}
                            />
                            <div className="flex items-center gap-2 mt-2 pl-1">
                                <Checkbox
                                    id={`reinc-${fullKey}`}
                                    checked={isReincident}
                                    onCheckedChange={(v) => onReincidentToggle?.(fullKey, !!v)}
                                    className="border-red-300 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600 w-3.5 h-3.5"
                                    data-testid={`reinc-${fullKey}`}
                                />
                                <label
                                    htmlFor={`reinc-${fullKey}`}
                                    className={`text-[11px] cursor-pointer select-none font-semibold ${isReincident ? "text-red-600" : "text-zinc-400"}`}
                                >
                                    Marcar como reincidente
                                </label>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}

// -------- Executive Summary (Step 7) --------
const PAYMENT_LABEL = {
    efectivo: "Efectivo",
    cheque: "Cheque",
    pos: "POS",
    enlace: "Enlace de pago",
    credito: "Crédito",
};
const FOLLOWUP_LABEL = {
    "8_dias": "En 8 días",
    "15_dias": "En 15 días",
    "otro": "Otro",
};

function countYesNo(items, data) {
    let yes = 0, no = 0, na = 0;
    items.forEach((it) => {
        if (data[it.k] === true) yes++;
        else if (data[it.k] === false) no++;
        else na++;
    });
    return { yes, no, na };
}

function SummaryBlock({ title, items, data, testId, prefix, reincidentChecks }) {
    const { yes, no } = countYesNo(items, data);
    const noItems = items.filter((it) => data[it.k] === false);
    const total = items.length;
    const allGood = yes === total;
    const hasIssues = no > 0;

    return (
        <Accordion type="single" collapsible className="rounded-lg border bg-white" data-testid={testId}>
            <AccordionItem value="block" className="border-b-0">
                <AccordionTrigger
                    className="px-3 py-2 hover:no-underline bg-zinc-50 rounded-t-lg data-[state=closed]:rounded-b-lg"
                    data-testid={`${testId}-trigger`}
                >
                    <div className="flex items-center justify-between w-full pr-2 gap-2">
                        <h3 className="text-sm font-semibold text-left leading-tight">{title}</h3>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full bg-[#FFD62B] text-black">
                                {yes} Sí
                            </span>
                            <span
                                className={`text-[11px] font-mono font-semibold px-2 py-0.5 rounded-full ${
                                    hasIssues ? "bg-[#FF0000]/10 text-[#FF0000]" : "bg-zinc-200 text-zinc-600"
                                }`}
                            >
                                {no} No
                            </span>
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="px-0 pb-0">
                    {allGood ? (
                        <div className="px-3 py-3 border-t bg-[#FFD62B]/10">
                            <p className="text-xs font-semibold text-black flex items-center gap-1.5">
                                <Check className="w-3.5 h-3.5" /> Todo en orden — {total}/{total}
                            </p>
                            <p className="text-[11px] text-foreground/70 mt-0.5">No hay observaciones para esta sección.</p>
                        </div>
                    ) : (
                        <div className="border-t">
                            <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#FF0000] font-bold">
                                Observaciones ({no})
                            </p>
                            <ul className="divide-y">
                                {noItems.map((it) => {
                                    const fullKey = prefix ? `${prefix}.${it.k}` : it.k;
                                    const isReinc = reincidentChecks?.has(fullKey);
                                    return (
                                        <li key={it.k} className={`px-3 py-2 flex items-start gap-2 ${isReinc ? "bg-red-50" : ""}`}>
                                            <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full shrink-0 ${isReinc ? "bg-red-600" : "bg-[#FF0000]"}`} />
                                            <span className="text-xs text-foreground/90 leading-snug flex-1">{it.finding || it.label}</span>
                                            {isReinc && (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-100 rounded-full px-2 py-0.5 shrink-0">
                                                    <RotateCcw className="w-2.5 h-2.5" /> Reincidente
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}

function ExecutiveSummary({ service, client, exterior, interior, food, notes, products, photos, paymentType, followUp, followUpPeriod, followUpDate, followUpOther, reincidentChecks }) {
    const clientName = client?.name || service?.client_name || "Cliente";
    const clientAddress = client?.address || service?.client_address || "";
    const dateStr = service?.scheduled_date || "";
    const formatDateDMY = (iso) => { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };
    const followUpText = followUp
        ? (followUpPeriod === "otro"
            ? (followUpOther ? `Sí — ${followUpOther}` : "Sí — Otro")
            : `Sí — ${FOLLOWUP_LABEL[followUpPeriod] || ""}${followUpDate ? ` · ${formatDateDMY(followUpDate)}` : ""}`)
        : "No";

    const noteFields = [
        { k: "areas_treated", label: "Áreas tratadas" },
        { k: "recommendations", label: "Recomendaciones" },
        { k: "problem_areas", label: "Áreas con problemas" },
        { k: "corrective_actions", label: "Acciones correctivas" },
        { k: "follow_ups", label: "Seguimientos / refuerzos" },
    ];
    const filledNotes = noteFields.filter((f) => (notes?.[f.k] || "").trim().length > 0);

    return (
        <Card className="p-4" data-testid="executive-summary">
            <div className="flex items-start justify-between mb-3">
                <div>
                    <h2 className="font-semibold text-lg leading-tight">Resumen del servicio</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Revise con el cliente antes de firmar</p>
                </div>
                <Badge className="bg-black text-white hover:bg-black">Paso 7 de 7</Badge>
            </div>

            {/* Cliente */}
            <div className="rounded-lg border bg-zinc-50 p-3 mb-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Cliente</p>
                <p className="font-semibold text-sm leading-snug">{clientName}</p>
                {clientAddress && <p className="text-xs text-foreground/80 leading-snug">{clientAddress}</p>}
                {dateStr && <p className="text-[11px] text-muted-foreground mt-1 font-mono">Fecha: {dateStr}</p>}
            </div>

            {/* Tiempos del servicio */}
            {service?.entry_time && (() => {
                const supportCount = (service.support_technician_ids || []).length;
                // Use "now" as a projected end time for display; final values are computed server-side after Finalizar
                const projected = computeServiceTimes(service.entry_time, new Date().toISOString(), supportCount);
                return (
                    <div className="rounded-lg border bg-white mb-3" data-testid="summary-time">
                        <div className="px-3 py-2 border-b bg-zinc-50 rounded-t-lg flex items-center justify-between">
                            <h3 className="text-sm font-semibold flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Tiempos del servicio</h3>
                        </div>
                        <div className="divide-y">
                            <div className="px-3 py-2 flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Hora de inicio</span>
                                <span className="text-xs font-mono font-semibold">{formatTime(service.entry_time)}</span>
                            </div>
                            <div className="px-3 py-2 flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Duración estimada hasta ahora</span>
                                <span className="text-xs font-mono font-semibold">{projected.durationLabel}</span>
                            </div>
                            {projected.totalTechnicians > 1 && (
                                <div className="px-3 py-2 flex items-center justify-between bg-[#FFD62B]/10">
                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                        <Users className="w-3 h-3" /> Horas hombre ({projected.totalTechnicians} técnicos)
                                    </span>
                                    <span className="text-xs font-mono font-semibold">{projected.manHoursLabel}</span>
                                </div>
                            )}
                            <div className="px-3 py-2 flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Hora de fin</span>
                                <span className="text-[11px] text-muted-foreground italic">Se registra al tocar &quot;Finalizar&quot;</span>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Inspecciones */}
            <div className="space-y-3 mb-3">
                <SummaryBlock title="Condiciones Exteriores" items={EXTERIOR} data={exterior} testId="summary-exterior" prefix="exterior" reincidentChecks={reincidentChecks} />
                <SummaryBlock title="Condiciones Interiores" items={INTERIOR} data={interior} testId="summary-interior" prefix="interior" reincidentChecks={reincidentChecks} />
                <SummaryBlock title="Bodega de Alimentos" items={FOOD} data={food} testId="summary-food" prefix="food" reincidentChecks={reincidentChecks} />
            </div>

            {/* Notas */}
            {filledNotes.length > 0 && (
                <div className="rounded-lg border bg-white mb-3" data-testid="summary-notes">
                    <div className="px-3 py-2 border-b bg-zinc-50 rounded-t-lg">
                        <h3 className="text-sm font-semibold">Notas del servicio</h3>
                    </div>
                    <div className="divide-y">
                        {filledNotes.map((f) => (
                            <div key={f.k} className="px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{f.label}</p>
                                <p className="text-xs text-foreground/90 whitespace-pre-wrap leading-snug">{notes[f.k]}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Productos */}
            <div className="rounded-lg border bg-white mb-3" data-testid="summary-products">
                <div className="flex items-center justify-between px-3 py-2 border-b bg-zinc-50 rounded-t-lg">
                    <h3 className="text-sm font-semibold">Productos utilizados</h3>
                    <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-700 font-semibold">
                        {products.length}
                    </span>
                </div>
                {products.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-3 py-3">Sin productos registrados</p>
                ) : (
                    <ul className="divide-y">
                        {products.map((p, i) => (
                            <li key={i} className="px-3 py-2">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">{p.product_name || `Producto #${i + 1}`}</p>
                                        <p className="text-[11px] text-muted-foreground">
                                            {p.quantity || 0}{p.unit ? ` ${p.unit}` : ""}{p.dose ? ` · ${p.dose}` : ""}{p.pest_target ? ` · ${p.pest_target}` : ""}
                                        </p>
                                    </div>
                                    <Badge variant="outline" className="font-mono text-[10px] shrink-0">{p.method}</Badge>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Fotos */}
            {photos.length > 0 && (
                <div className="rounded-lg border bg-white mb-3 px-3 py-2 flex items-center justify-between" data-testid="summary-photos">
                    <h3 className="text-sm font-semibold">Fotografías adjuntas</h3>
                    <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-[#FFD62B] text-black font-semibold">{photos.length}</span>
                </div>
            )}

            {/* Pago y seguimiento */}
            <div className="rounded-lg border bg-white mb-1" data-testid="summary-payment">
                <div className="px-3 py-2 border-b bg-zinc-50 rounded-t-lg">
                    <h3 className="text-sm font-semibold">Pago y seguimiento</h3>
                </div>
                <div className="divide-y">
                    <div className="px-3 py-2 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Tipo de pago</span>
                        <span className="text-xs font-semibold">{paymentType ? PAYMENT_LABEL[paymentType] : "—"}</span>
                    </div>
                    <div className="px-3 py-2 flex items-start justify-between gap-3">
                        <span className="text-xs text-muted-foreground shrink-0">Refuerzo</span>
                        <div className="text-right">
                            <span className="text-xs font-semibold">{followUpText}</span>
                            {followUp && followUpDate && (
                                <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#FFD62B]/20 border border-[#FFD62B]/50 px-2 py-0.5 text-[10px] font-bold text-black">
                                    {formatDateDMY(followUpDate)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
}
